import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Bell, Send, Mail, Check, AlertTriangle, Zap, Target, Radio, Shield, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_OPTIONS = [
    { key: 'prediction_ready', label: 'Prediction Ready', desc: 'When God Mode generates a new prediction', icon: Target },
    { key: 'match_start', label: 'Match Start', desc: 'When a live match begins', icon: Radio },
    { key: 'match_end', label: 'Match End', desc: 'When a match concludes with results', icon: Check },
    { key: 'scraper_complete', label: 'Scraper Complete', desc: 'When autonomous intelligence scraping finishes', icon: Shield },
    { key: 'accuracy_milestone', label: 'Accuracy Milestone', desc: 'When model accuracy hits a new high', icon: TrendingUp },
    { key: 'impact_player_detected', label: 'Impact Player Detected', desc: 'Real-time impact player substitution', icon: Zap },
    { key: 'win_probability_shift', label: 'Win % Shift', desc: 'Major win probability change during live match', icon: AlertTriangle },
];

export default function NotificationAlerts() {
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        Promise.all([
            API.get('/notifications/preferences'),
            API.get('/notifications/history').catch(() => ({ data: { history: [] } })),
        ]).then(([prefRes, histRes]) => {
            setPrefs(prefRes.data);
            setHistory(histRes.data?.history || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await API.post('/notifications/preferences', prefs);
            toast.success('Notification preferences saved');
        } catch (e) {
            toast.error('Failed to save');
        }
        setSaving(false);
    };

    const testTelegram = async () => {
        if (!prefs?.telegram_bot_token) {
            toast.error('Enter bot token first');
            return;
        }
        setTesting(true);
        try {
            const res = await API.post('/notifications/test-telegram', {
                bot_token: prefs.telegram_bot_token,
                chat_id: prefs.telegram_chat_id || '',
            });
            if (res.data?.success) {
                toast.success(res.data.message || 'Test message sent!');
                // Auto-fill detected chat_id
                if (res.data.detected_chat_id && res.data.detected_chat_id !== prefs.telegram_chat_id) {
                    setPrefs(p => ({ ...p, telegram_chat_id: res.data.detected_chat_id }));
                    toast.info(`Chat ID auto-detected: ${res.data.detected_chat_id}`);
                }
            } else {
                toast.error(res.data?.error || 'Failed to send');
                // Auto-fill if backend detected the correct chat ID
                if (res.data?.detected_chat_id) {
                    setPrefs(p => ({ ...p, telegram_chat_id: res.data.detected_chat_id }));
                    toast.info(`Correct Chat ID detected: ${res.data.detected_chat_id} — click test again`);
                }
            }
        } catch (e) {
            toast.error('Telegram test failed — check your bot token');
        }
        setTesting(false);
    };

    const toggleAlert = (key) => {
        setPrefs(p => ({
            ...p,
            alerts: { ...p.alerts, [key]: !p.alerts?.[key] },
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="notification-alerts-page">
            {/* Header */}
            <div className="glass-card p-6">
                <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-amber-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">NOTIFICATION ALERTS</h2>
                    <span className="text-[9px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded tech-mono">CONFIGURE</span>
                </div>
                <p className="text-xs text-white/30 mt-2">Set up Telegram and email alerts for match events, predictions, and intelligence updates.</p>
            </div>

            {/* Telegram Setup */}
            <div className="glass-card p-6 border border-blue-500/20" data-testid="telegram-config">
                <div className="flex items-center gap-2 mb-4">
                    <Send className="w-4 h-4 text-blue-400" />
                    <h3 className="text-sm text-white font-semibold">Telegram Integration</h3>
                    <label className="ml-auto flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-white/40">Enabled</span>
                        <div className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${prefs?.telegram_enabled ? 'bg-blue-500' : 'bg-white/10'}`}
                            onClick={() => setPrefs(p => ({ ...p, telegram_enabled: !p.telegram_enabled }))}>
                            <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${prefs?.telegram_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </div>
                    </label>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-white/40 mb-1 block">Bot Token</label>
                        <input type="password" data-testid="telegram-bot-token"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-400 focus:outline-none"
                            placeholder="Enter your Telegram bot token"
                            value={prefs?.telegram_bot_token || ''}
                            onChange={e => setPrefs(p => ({ ...p, telegram_bot_token: e.target.value }))} />
                        <p className="text-[10px] text-white/20 mt-1">Get from @BotFather on Telegram</p>
                    </div>
                    <div>
                        <label className="text-xs text-white/40 mb-1 block">Chat ID <span className="text-white/20">(optional — auto-detected on test)</span></label>
                        <input type="text" data-testid="telegram-chat-id"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-400 focus:outline-none"
                            placeholder="Leave empty to auto-detect, or enter numeric ID"
                            value={prefs?.telegram_chat_id || ''}
                            onChange={e => setPrefs(p => ({ ...p, telegram_chat_id: e.target.value }))} />
                        <p className="text-[10px] text-white/20 mt-1">Auto-detected when you click test, or get manually from @userinfobot</p>
                    </div>
                    <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/10 text-xs text-blue-400/80">
                        <strong>Setup:</strong> 1) Paste your bot token above. 2) Open your bot in Telegram and send <code className="bg-blue-500/20 px-1 rounded">/start</code>. 3) Click test below — Chat ID will be auto-detected.
                    </div>
                    <button onClick={testTelegram} disabled={testing} data-testid="test-telegram-btn"
                        className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs hover:bg-blue-500/30 transition-colors disabled:opacity-50">
                        {testing ? 'Sending...' : 'Send Test Message'}
                    </button>
                </div>
            </div>

            {/* Email Setup */}
            <div className="glass-card p-6 border border-purple-500/20" data-testid="email-config">
                <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-4 h-4 text-purple-400" />
                    <h3 className="text-sm text-white font-semibold">Email Alerts</h3>
                    <label className="ml-auto flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-white/40">Enabled</span>
                        <div className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${prefs?.email_enabled ? 'bg-purple-500' : 'bg-white/10'}`}
                            onClick={() => setPrefs(p => ({ ...p, email_enabled: !p.email_enabled }))}>
                            <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${prefs?.email_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </div>
                    </label>
                </div>
                <div>
                    <label className="text-xs text-white/40 mb-1 block">Email Address</label>
                    <input type="email" data-testid="email-address"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-400 focus:outline-none"
                        placeholder="your@email.com"
                        value={prefs?.email_address || ''}
                        onChange={e => setPrefs(p => ({ ...p, email_address: e.target.value }))} />
                </div>
            </div>

            {/* Alert Types */}
            <div className="glass-card p-6" data-testid="alert-types">
                <h3 className="text-sm text-white font-semibold mb-4">Alert Types</h3>
                <div className="space-y-2">
                    {ALERT_OPTIONS.map(opt => (
                        <div key={opt.key} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${prefs?.alerts?.[opt.key] ? 'bg-white/5 border border-cyan-500/20' : 'bg-white/[0.02] border border-white/5'}`}
                            onClick={() => toggleAlert(opt.key)} data-testid={`alert-toggle-${opt.key}`}>
                            <div className="flex items-center gap-3">
                                <opt.icon className={`w-4 h-4 ${prefs?.alerts?.[opt.key] ? 'text-cyan-400' : 'text-white/20'}`} />
                                <div>
                                    <div className="text-sm text-white">{opt.label}</div>
                                    <div className="text-[10px] text-white/30">{opt.desc}</div>
                                </div>
                            </div>
                            <div className={`w-10 h-5 rounded-full transition-colors relative ${prefs?.alerts?.[opt.key] ? 'bg-cyan-500' : 'bg-white/10'}`}>
                                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${prefs?.alerts?.[opt.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button onClick={save} disabled={saving} data-testid="save-notification-prefs"
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Preferences'}
                </button>
            </div>

            {/* Notification History */}
            {history.length > 0 && (
                <div className="glass-card p-6" data-testid="notification-history">
                    <h3 className="text-sm text-white font-semibold mb-4">Recent Notifications</h3>
                    <div className="space-y-2">
                        {history.slice(0, 10).map((h, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5">
                                <div>
                                    <div className="text-sm text-white">{h.title}</div>
                                    <div className="text-[10px] text-white/30">{h.message?.slice(0, 80)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] text-white/20">{h.sent_at?.slice(0, 16)}</div>
                                    <div className="text-[10px] text-cyan-400">{h.channels} channels</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
